import { Link } from 'react-router'
import { ArrowRight } from 'lucide-react'

interface VoirAussiLink {
  href: string
  label: string
  desc?: string
}

interface VoirAussiProps {
  title?: string
  links: VoirAussiLink[]
}

/**
 * Reusable internal-linking block — "Voir aussi" / "Pages similaires".
 * Place at the bottom of content pages to boost crawl depth and cross-
 * pollinate SEO authority between related pages.
 *
 * Design: compact pill grid (matches footer contextLinks pattern).
 */
export function VoirAussi({ title = 'Voir aussi', links }: VoirAussiProps) {
  if (links.length === 0) return null

  return (
    <section className="mt-12 rounded-2xl border border-border bg-slate-50 p-6">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {links.map((link) => (
          <Link
            key={link.href}
            to={link.href}
            className="group flex items-start justify-between gap-2 rounded-xl bg-white p-3 ring-1 ring-slate-200 transition-all hover:ring-primary"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900 group-hover:text-primary">{link.label}</p>
              {link.desc && <p className="mt-0.5 text-xs text-slate-500 line-clamp-1">{link.desc}</p>}
            </div>
            <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-300 group-hover:text-primary" />
          </Link>
        ))}
      </div>
    </section>
  )
}

/**
 * Pre-built cross-link sets for common page types.
 * Import and spread where needed:
 *   <VoirAussi links={CROSS_LINKS.cee} />
 */
export const CROSS_LINKS = {
  renovation: [
    { href: '/cee', label: 'Primes CEE 2026', desc: '19 opérations subventionnées' },
    { href: '/rge', label: 'Artisans RGE', desc: 'Qualifications et vérification' },
    { href: '/simulateur-aides', label: 'Simulateur aides', desc: 'Calculez vos primes' },
    { href: '/renovation-energetique', label: 'Rénovation énergétique', desc: 'Guide complet' },
  ],
  services: [
    { href: '/devis-gratuit', label: 'Devis gratuit', desc: '3 devis en 48h' },
    { href: '/guide-prix', label: 'Guide des prix', desc: 'Tarifs par métier' },
    { href: '/carte-artisans', label: 'Carte artisans', desc: 'Vue géographique' },
    { href: '/metier', label: 'Fiches métiers', desc: 'Formations et salaires' },
  ],
  geo: [
    { href: '/regions', label: 'Par région', desc: '18 régions' },
    { href: '/departements', label: 'Par département', desc: '101 départements' },
    { href: '/toutes-villes', label: 'Par ville', desc: 'Toutes les communes' },
    { href: '/carte-artisans', label: 'Carte interactive', desc: 'Leaflet map' },
  ],
  inspiration: [
    { href: '/avant-apres', label: 'Avant / Après', desc: 'Galerie de projets' },
    { href: '/blog', label: 'Blog', desc: 'Conseils et actualités' },
    { href: '/comparaison', label: 'Comparatifs', desc: 'Produits et solutions' },
    { href: '/barometre', label: 'Baromètre prix', desc: 'Tendances du marché' },
  ],
}
